import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { openOAuthPopup } from '../utils/AuthUtils';
import { config } from '../Constants';

const URL = config.url;
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [authToken, setAuthToken] = useState(() => {
        const token = localStorage.getItem('authToken');
        return token;
    });

    const needsOnboarding = useMemo(() => {
        return Boolean(
            user?.premium &&
            user?.subscriptionStatus !== 'canceled' &&
            !user?.hasCompletedOnboarding
        );
    }, [user]);

    const logout = useCallback(async () => {
        try {
            await fetch(`${URL}/api/v1/auth/logout`, {
                method: 'POST',
                headers: {
                    'Authorization': authToken ? `Bearer ${authToken}` : '',
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });
        } catch (error) {
            console.error('Logout error:', error);
        }
        localStorage.removeItem('authToken');
        setAuthToken(null);
        setUser(null);
    }, [authToken]);

    const validateToken = useCallback(async () => {
        if (!authToken) {
            setLoading(false);
            return false;
        }

        try {
            const response = await fetch(`${URL}/api/v1/auth/me`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();   
                if (data.result === 'SUCCESS') {
                    setUser(data.data);
                    setLoading(false);
                    return true;
                }
            }
            
            throw new Error('Token validation failed');
        } catch (error) {
            localStorage.removeItem('authToken');
            setAuthToken(null);
            setUser(null);
            setLoading(false);
            return false;
        }
    }, [authToken]);

    const loginSuccess = useCallback(async (token) => {
        if (!token) {
            console.error("No token provided to loginSuccess");
            return false;
        }
        
        localStorage.setItem('authToken', token);
        setAuthToken(token);
        return await validateToken();
    }, [validateToken]);

    useEffect(() => {
        validateToken();
    }, [validateToken]);

    useEffect(() => {
        if (user) {
            const interval = setInterval(() => {
                validateToken();
            }, 5 * 60 * 1000);
            
            return () => clearInterval(interval);
        }
    }, [user, validateToken]);

    const login = useCallback((redirectPath) => {
        openOAuthPopup(loginSuccess, redirectPath);
    }, [loginSuccess]);

    const completeOnboarding = useCallback(async () => {
        if (!user?.id || !authToken) {
            console.error('Cannot complete onboarding: user or token missing');
            return;
        }

        try {
            const response = await fetch(`${URL}/api/v1/auth/onboarding/complete`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.ok) {
                console.log('Onboarding completed successfully');
                await validateToken();
            } else {
                console.error('Failed to complete onboarding');
            }
        } catch (error) {
            console.error('Error completing onboarding:', error);
        }
    }, [user, authToken, validateToken]);

    const isPremiumStatus = !!user && user.premium && user.subscriptionStatus !== 'canceled';
    const isAdmin = user?.role === 'ADMIN';

    const contextValue = useMemo(() => ({
        user,
        authToken,
        isAuthenticated: !!user,
        isPremium: isPremiumStatus,
        isAdmin,
        loading,
        needsOnboarding,
        login,
        loginSuccess,
        logout,
        validateToken,
        completeOnboarding
    }), [user, authToken, isPremiumStatus, isAdmin, loading, needsOnboarding, login, loginSuccess, logout, validateToken, completeOnboarding]);

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
